import { useState } from 'react';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ArrowLeft, Bluetooth, Check, AlertTriangle, Save } from 'lucide-react';
import { toast } from 'sonner';
import { nursesApi } from '../lib/api/nurses';

interface NurseVitalsChecklistProps {
  onBack: () => void;
  selectedPatient?: any;
}

export function NurseVitalsChecklist({ onBack, selectedPatient }: NurseVitalsChecklistProps) {
  const [bleConnected, setBleConnected] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vitals, setVitals] = useState({
    heartRate: '',
    systolic: '',
    diastolic: '',
    spo2: '',
    temperature: '',
    respiratoryRate: '',
    weight: '',
    height: ''
  });

  const [checklist, setChecklist] = useState({
    patientIdentified: false,
    consentObtained: false,
    handsWashed: false,
    equipmentSanitized: false,
    vitalsRecorded: false,
    patientComfortable: false
  });

  const handleConnectBLE = () => {
    toast.info('Connecting to Bluetooth device...');
    setTimeout(() => {
      setBleConnected(true);
      // Simulate receiving live data
      setVitals({
        ...vitals,
        heartRate: '72',
        spo2: '98',
        temperature: '98.4'
      });
      toast.success('Connected to vitals monitor');
    }, 1500);
  };

  const handleSave = async () => {
    const allChecklistComplete = Object.values(checklist).every(v => v);
    const allVitalsEntered = vitals.heartRate && vitals.systolic && vitals.diastolic && vitals.spo2;

    if (!allChecklistComplete) {
      toast.error('Please complete all checklist items');
      return;
    }

    if (!allVitalsEntered) {
      toast.error('Please enter all vital signs');
      return;
    }

    if (!selectedPatient?.id) {
      toast.error('No patient selected');
      return;
    }

    setIsSubmitting(true);
    try {
      await nursesApi.submitVitals({
        patient_id: selectedPatient.id,
        heart_rate: parseInt(vitals.heartRate),
        systolic_bp: parseInt(vitals.systolic),
        diastolic_bp: parseInt(vitals.diastolic),
        spo2: parseInt(vitals.spo2),
        temperature: vitals.temperature ? parseFloat(vitals.temperature) : undefined,
        respiratory_rate: vitals.respiratoryRate ? parseInt(vitals.respiratoryRate) : undefined,
        weight: vitals.weight ? parseFloat(vitals.weight) : undefined,
        height: vitals.height ? parseFloat(vitals.height) : undefined,
        notes: `Collected by nurse during home visit. All checklist items completed.`
      });
      
      toast.success('Vitals saved successfully!', {
        description: 'Data synced to patient record'
      });
      
      setTimeout(() => {
        onBack();
      }, 1500);
    } catch (error: any) {
      toast.error('Failed to save vitals', {
        description: error?.message || 'Please try again'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isOutOfRange = (param: string, value: string) => {
    const val = parseFloat(value);
    if (isNaN(val)) return false;

    switch (param) {
      case 'heartRate':
        return val < 60 || val > 100;
      case 'systolic':
        return val < 90 || val > 140;
      case 'diastolic':
        return val < 60 || val > 90;
      case 'spo2':
        return val < 95;
      case 'temperature':
        return val < 97 || val > 99.5;
      case 'respiratoryRate':
        return val < 12 || val > 20;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-[#D4F0F8] pb-20">
      {/* Header */}
      <div className="bg-primary p-6 rounded-b-3xl shadow-md">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="text-primary-foreground">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-primary-foreground">Vitals Collection</h1>
            {selectedPatient ? (
              <p className="text-primary-foreground/80">
                Patient: {selectedPatient.name} ({selectedPatient.id})
              </p>
            ) : (
              <p className="text-primary-foreground/80">No patient selected</p>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* BLE Connection */}
        <Card className="shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bluetooth className={`w-6 h-6 ${bleConnected ? 'text-[#14CAF4]' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-foreground">Bluetooth Vitals Monitor</p>
                  <p className="text-muted-foreground text-sm">
                    {bleConnected ? 'Connected - Live monitoring' : 'Not connected'}
                  </p>
                </div>
              </div>
              {!bleConnected ? (
                <Button onClick={handleConnectBLE}>
                  Connect
                </Button>
              ) : (
                <Badge className="bg-[#14CAF4] text-white">
                  <Check className="w-4 h-4 mr-1" />
                  Connected
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pre-Visit Checklist */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary">Pre-Visit Checklist</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { key: 'patientIdentified', label: 'Patient identity verified (name, ID, DOB)' },
              { key: 'consentObtained', label: 'Informed consent obtained for procedures' },
              { key: 'handsWashed', label: 'Hands washed/sanitized' },
              { key: 'equipmentSanitized', label: 'All equipment sanitized and calibrated' }
            ].map((item) => (
              <div key={item.key} className="flex items-start gap-3 p-3 bg-secondary rounded-lg">
                <Checkbox
                  id={item.key}
                  checked={checklist[item.key as keyof typeof checklist]}
                  onCheckedChange={(checked) =>
                    setChecklist({ ...checklist, [item.key]: checked as boolean })
                  }
                />
                <label
                  htmlFor={item.key}
                  className="text-foreground cursor-pointer flex-1"
                >
                  {item.label}
                </label>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Vital Signs */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary">Vital Signs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Heart Rate (bpm)</Label>
                <div className="relative">
                  <Input
                    type="number"
                    value={vitals.heartRate}
                    onChange={(e) => setVitals({ ...vitals, heartRate: e.target.value })}
                    placeholder="60-100"
                    className={`mt-2 ${isOutOfRange('heartRate', vitals.heartRate) ? 'border-destructive' : ''}`}
                  />
                  {isOutOfRange('heartRate', vitals.heartRate) && (
                    <AlertTriangle className="w-4 h-4 text-destructive absolute right-3 top-5" />
                  )}
                </div>
                {isOutOfRange('heartRate', vitals.heartRate) && (
                  <p className="text-destructive text-sm mt-1">Out of normal range</p>
                )}
              </div>

              <div>
                <Label>Blood Pressure (mmHg)</Label>
                <div className="flex gap-2 mt-2">
                  <div className="relative flex-1">
                    <Input
                      type="number"
                      value={vitals.systolic}
                      onChange={(e) => setVitals({ ...vitals, systolic: e.target.value })}
                      placeholder="Systolic"
                      className={isOutOfRange('systolic', vitals.systolic) ? 'border-destructive' : ''}
                    />
                  </div>
                  <span className="text-muted-foreground mt-2">/</span>
                  <div className="relative flex-1">
                    <Input
                      type="number"
                      value={vitals.diastolic}
                      onChange={(e) => setVitals({ ...vitals, diastolic: e.target.value })}
                      placeholder="Diastolic"
                      className={isOutOfRange('diastolic', vitals.diastolic) ? 'border-destructive' : ''}
                    />
                  </div>
                </div>
                {(isOutOfRange('systolic', vitals.systolic) || isOutOfRange('diastolic', vitals.diastolic)) && (
                  <p className="text-destructive text-sm mt-1">Out of normal range</p>
                )}
              </div>

              <div>
                <Label>SpO2 (%)</Label>
                <div className="relative">
                  <Input
                    type="number"
                    value={vitals.spo2}
                    onChange={(e) => setVitals({ ...vitals, spo2: e.target.value })}
                    placeholder="95-100"
                    className={`mt-2 ${isOutOfRange('spo2', vitals.spo2) ? 'border-destructive' : ''}`}
                  />
                  {isOutOfRange('spo2', vitals.spo2) && (
                    <AlertTriangle className="w-4 h-4 text-destructive absolute right-3 top-5" />
                  )}
                </div>
                {isOutOfRange('spo2', vitals.spo2) && (
                  <p className="text-destructive text-sm mt-1">Critical - Below 95%</p>
                )}
              </div>

              <div>
                <Label>Temperature (°F)</Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.1"
                    value={vitals.temperature}
                    onChange={(e) => setVitals({ ...vitals, temperature: e.target.value })}
                    placeholder="97-99.5"
                    className={`mt-2 ${isOutOfRange('temperature', vitals.temperature) ? 'border-destructive' : ''}`}
                  />
                  {isOutOfRange('temperature', vitals.temperature) && (
                    <AlertTriangle className="w-4 h-4 text-destructive absolute right-3 top-5" />
                  )}
                </div>
              </div>

              <div>
                <Label>Respiratory Rate (breaths/min)</Label>
                <div className="relative">
                  <Input
                    type="number"
                    value={vitals.respiratoryRate}
                    onChange={(e) => setVitals({ ...vitals, respiratoryRate: e.target.value })}
                    placeholder="12-20"
                    className={`mt-2 ${isOutOfRange('respiratoryRate', vitals.respiratoryRate) ? 'border-destructive' : ''}`}
                  />
                  {isOutOfRange('respiratoryRate', vitals.respiratoryRate) && (
                    <AlertTriangle className="w-4 h-4 text-destructive absolute right-3 top-5" />
                  )}
                </div>
              </div>

              <div>
                <Label>Weight (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={vitals.weight}
                  onChange={(e) => setVitals({ ...vitals, weight: e.target.value })}
                  placeholder="Enter weight"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Height (cm)</Label>
                <Input
                  type="number"
                  value={vitals.height}
                  onChange={(e) => setVitals({ ...vitals, height: e.target.value })}
                  placeholder="Enter height"
                  className="mt-2"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Post-Visit Checklist */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary">Post-Visit Checklist</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-secondary rounded-lg">
              <Checkbox
                id="vitalsRecorded"
                checked={checklist.vitalsRecorded}
                onCheckedChange={(checked) =>
                  setChecklist({ ...checklist, vitalsRecorded: checked as boolean })
                }
              />
              <label
                htmlFor="vitalsRecorded"
                className="text-foreground cursor-pointer flex-1"
              >
                All vitals recorded and verified
              </label>
            </div>
            <div className="flex items-start gap-3 p-3 bg-secondary rounded-lg">
              <Checkbox
                id="patientComfortable"
                checked={checklist.patientComfortable}
                onCheckedChange={(checked) =>
                  setChecklist({ ...checklist, patientComfortable: checked as boolean })
                }
              />
              <label
                htmlFor="patientComfortable"
                className="text-foreground cursor-pointer flex-1"
              >
                Patient is comfortable and questions answered
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onBack}>
            Cancel
          </Button>
          <Button className="flex-1 bg-accent hover:bg-accent/90" onClick={handleSave} disabled={isSubmitting}>
            <Save className="w-4 h-4 mr-2" />
            Save & Complete
          </Button>
        </div>
      </div>
    </div>
  );
}